import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import {
  Clickable,
  Icon,
  Button,
  Checkbox,
} from '@fashionnovateam/nova-ui-toolkit';
import { pick } from 'lodash';
import {
  useCreateDocumentLink,
  useCreateVendorDocument,
  useGetVendorDocumentStatus,
  useGetVendorDocumentStatusCompleted,
} from '../../Lib/Hooks/vendor.pandadoc.hooks';
import { PageLoading } from '../PageLoading/PageLoading';
import toast from '../Toast/Toast';
import { legalTextDetails } from './LegalTexts';
import { CreateVendorDocumentData } from '../../Lib/Api/vendor.pandadoc';

export type PandadocModalProps = {
  userId: number;
  documentName: string;
  open: boolean;
  onClose: () => void;
  onCompletion: () => Promise<void>;
  individual?: boolean;
  form: any;
};

function PandadocModalStatus({
  userId,
  documentName,
  open,
  onCompletion,
  pandadocData,
  onClose,
}: PandadocModalProps & {
  pandadocData: any;
}) {
  const { data: pandadocStatus } = useGetVendorDocumentStatus(
    userId,
    documentName,
    pandadocData?.pandadoc_id,
  );
  const { data: pandadocStatusCompleted } = useGetVendorDocumentStatusCompleted(
    userId,
    documentName,
    pandadocData?.pandadoc_id,
  );
  const { mutate: createDocumentLink, data: documentLinkData } =
    useCreateDocumentLink();
  const [isFrontCompleted, setIsFrontCompleted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (
      open &&
      isFrontCompleted &&
      pandadocStatusCompleted &&
      pandadocData &&
      pandadocStatusCompleted.pandadoc_id &&
      pandadocStatusCompleted.status.includes('completed') &&
      pandadocStatusCompleted.signed_at
    ) {
      setIsFrontCompleted(false);
      onCompletion();
    }
  }, [pandadocStatusCompleted]);

  const handleMessage = async (e: any) => {
    if (!open || !e || !e.data) return;
    const type = e.data && e.data.type;

    if (type === 'session_view.document.completed') {
      setIsFrontCompleted(true);
      setIsLoading(true);
    }
  };

  useEffect(() => {
    window.addEventListener('message', handleMessage);
    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [handleMessage]);

  useEffect(() => {
    if (
      open &&
      pandadocStatus &&
      pandadocData &&
      pandadocStatus.pandadoc_id &&
      pandadocStatus.status &&
      !pandadocStatus.status.includes('draft') &&
      !pandadocStatus.status.includes('uploaded')
    ) {
      createDocumentLink(
        { userId, documentId: pandadocStatus.pandadoc_id },
        {
          onError: (err) => {
            console.error('Error creating pandadoc link', err);
            onClose();
            toast('Error generating document, please try again later.', {
              type: 'error',
            });
          },
        },
      );
    }
  }, [pandadocStatus]);

  return (
    <div className="h-full w-full">
      {documentLinkData && documentLinkData.id && !isLoading ? (
        <iframe
          title="Vendor WX Embed"
          src={`https://app.pandadoc.com/s/${documentLinkData.id}`}
          width="100%"
          height="100%"
        />
      ) : (
        <PageLoading />
      )}
    </div>
  );
}

type PandadocLegalTextProps = Pick<
  PandadocModalProps,
  'documentName' | 'individual'
> & {
  setAcceptedTerms: React.Dispatch<React.SetStateAction<boolean>>;
};

const getLegalTextByName = (documentName: string, individual: boolean) => {
  if (documentName === 'w9_form') {
    return legalTextDetails[
      individual ? 'w9_form_individual' : 'w9_form_company'
    ];
  }
  return legalTextDetails[documentName as keyof typeof legalTextDetails];
};

function PandadocLegalText({
  documentName,
  individual,
  setAcceptedTerms,
}: PandadocLegalTextProps) {
  const [legalText] = useState(
    getLegalTextByName(documentName, individual || false),
  );
  const [checked, setChecked] = useState(legalText.items.map(() => false));

  const documentNameToAgreeText = () => {
    if (documentName === 'w9_form') {
      return 'W9';
    }
    if (documentName === 'w8ben_form') {
      return 'W8-BEN';
    }
    if (documentName === 'w8bene_form') {
      return 'W8-BEN-E';
    }
    return 'WX';
  };

  return (
    <div className="max-w-prose text-center m-auto h-full overflow-y-auto">
      <div className="text-left text-base-disabled px-4 pt-4">
        <div>
          <ul className="ml-4">
            {legalText.items.map((item, key) => (
              <li className="mb-2 flex gap-2" key={item}>
                <Checkbox
                  checked={checked[key]}
                  onClick={() =>
                    setChecked((prev) =>
                      prev.map((v, i) => (i === key ? !v : v)),
                    )
                  }
                />
                {item}
              </li>
            ))}
          </ul>
        </div>
        <div className="sticky md:fixed bottom-0 left-0 text-center bg-neutral-0 w-full border-t border-b border-neutral-200">
          <Button
            disabled={!checked.every((v) => v)}
            className="my-4"
            onClick={() => {
              setAcceptedTerms(true);
            }}
          >
            Agree & Sign {documentNameToAgreeText()} Form
          </Button>
        </div>
      </div>
    </div>
  );
}

const buildCreateVendorDocumentBody = (
  documentName: string,
  form: any,
): CreateVendorDocumentData => {
  switch (documentName) {
    case 'w9_form':
      if (form.is_individual) {
        return pick(form, [
          'tax_classification',
          'first_name',
          'last_name',
          'middle_name',
          'address',
          'address_2',
          'city',
          'state',
          'zip',
          'tax_id',
          'is_individual',
          'company_name',
        ]);
      }
      return pick(form, [
        'tax_classification',
        'address',
        'address_2',
        'city',
        'state',
        'zip',
        'tax_id',
        'is_individual',
        'first_name',
        'last_name',
        'middle_name',
        'legal_name',
        'company_name',
      ]);
    case 'w8ben_form':
      return pick(form, [
        'first_name',
        'last_name',
        'middle_name',
        'address',
        'address_2',
        'city',
        'state',
        'zip',
        'payment_billing_address',
        'payment_billing_address_2',
        'payment_country',
        'payment_state',
        'payment_city',
        'payment_zip',
        'country',
        'is_individual',
      ]);
    case 'w8bene_form':
      return pick(form, [
        'legal_name',
        'company_name',
        'address',
        'address_2',
        'city',
        'state',
        'zip',
        'payment_country',
        'payment_billing_address',
        'payment_billing_address_2',
        'payment_city',
        'payment_state',
        'payment_zip',
        'country',
      ]);
    default:
      return form;
  }
};

export default function PandadocModal({
  userId,
  individual,
  documentName,
  onClose = () => {},
  open = false,
  form,
  onCompletion = async () => {},
}: PandadocModalProps) {
  const { mutate: createVendorDocument, data: pandadocData } =
    useCreateVendorDocument();
  const [acceptedTerms, setAcceptedTerms] = useState(false);

  const handleOnClose = () => {
    setAcceptedTerms(false);
    onClose();
  };

  useEffect(() => {
    if (open && acceptedTerms) {
      createVendorDocument(
        {
          userId,
          documentName,
          body: buildCreateVendorDocumentBody(documentName, form),
        },
        {
          onError: (err) => {
            console.error('Error creating pandadoc document', err);
            handleOnClose();
            toast('Error creating pandadoc document, please try again later.', {
              type: 'error',
            });
          },
        },
      );
    }
  }, [open, acceptedTerms]);

  if (!open) return null;

  return (
    <>
      <Helmet>
        <meta
          name="viewport"
          content="width=device-width, height=device-height, initial-scale=1, minimum-scale=1, maximum-scale=1, user-scalable=no, shrink-to-fit=no"
        />
      </Helmet>
      <button
        type="button"
        aria-label="modal-bg"
        onClick={handleOnClose}
        className="twp cursor-default fixed top-0 left-0 bg-base-disabled bg-opacity-50 z-50 h-full w-full font-['Geist']"
      />
      <div className="twp fixed md:top-[35px] top-0 left-0 z-[100] h-full w-full touch-pan-x touch-pan-y">
        <div className="cursor-default bg-neutral-0 h-full w-full rounded-t-xl border border-neutral-200 flex flex-col transition touch-pan-x touch-pan-y">
          <div className="pr-2 pl-2 pt-5 pb-5 w-full border-b border-neutral-200 touch-pan-x touch-pan-y">
            <Clickable
              className="text-primary-700 font-medium text-md flex items-center gap-3"
              onClick={handleOnClose}
            >
              <Icon name="arrow/arrow-left" className="w-6 h-6" />
              Back to Editing
            </Clickable>
          </div>
          {!acceptedTerms ? (
            <PandadocLegalText
              documentName={documentName}
              individual={individual}
              setAcceptedTerms={setAcceptedTerms}
            />
          ) : (
            <>
              <div className="w-full border-t border-b border-neutral-200 pr-3 pl-3 pt-5 pb-5 text-sm font-semibold text-black text-left">
                Please sign the form below to complete your application and
                after submission you will not be able to edit the vendor portal
                application.
              </div>
              {pandadocData ? (
                <PandadocModalStatus
                  form={form}
                  pandadocData={pandadocData}
                  open={open}
                  userId={userId}
                  documentName={documentName}
                  onCompletion={onCompletion}
                  onClose={handleOnClose}
                />
              ) : (
                <PageLoading />
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
}
